import * as React from 'react';
import { FloorPlanSubPage, MouseCoords, CropPosition, DragType, FloorPlanSubPageProps, FloorPlanSubPageState } from './FloorPlanSubPage';
import { FloorPlanInteractorType, FloorPlanInteractor } from './FloorPlanInteractor';
import { BaseColor } from '../../../constants/LayoutConstants';
import { FloorPlanStore } from '../../../stores/FloorPlanStore';
import { ProjectSettingsStore } from '../../../stores/ProjectSettingsStore';

export class FloorPlanCrop extends FloorPlanSubPage {
    protected static CROPLINEWIDTH: number = 4;
    protected baseZoom: number = 0;
    protected baseDistance: number = 0;

    private interactors: Map<FloorPlanInteractorType, FloorPlanInteractor> = new Map();

    get zoom(): number {return this.props.store.cropZoom; }
    set zoom(value: number) {this.props.store.cropZoom = value; }

    get imagePosition(): MouseCoords {return this.props.store.cropImagePosition; }
    set imagePosition(value: MouseCoords) {this.props.store.cropImagePosition = value; }

    public componentWillMount(): void {
        this.addStoreListener(FloorPlanStore, this.onChange);
        this.addStoreListener(ProjectSettingsStore, this.onChange);
        this.onChange();
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.onResize, false);
        window.addEventListener('pointerup', this.pointerUp, true);
        this.divElement.setAttribute('touch-action', 'none');
        this.divElement.addEventListener('pointermove', this.pointerMove, true);
        this.divElement.addEventListener('pointerdown', this.pointerDown, true);
        this.divElement.addEventListener('wheel', this.mouseWheel, true);
        this.divElement.addEventListener('contextmenu', this.contextMenu, false);
        this.updateDimensions();
        this.onChange();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        if (this.resizeTrigger) {
            window.clearTimeout(this.resizeTrigger);
        }
        window.removeEventListener('resize', this.onResize, false);
        window.removeEventListener('pointerup', this.pointerUp, true);
        this.divElement.removeEventListener('pointermove', this.pointerMove, true);
        this.divElement.removeEventListener('pointerdown', this.pointerDown, true);
        this.divElement.removeEventListener('wheel', this.mouseWheel, true);
        this.divElement.removeEventListener('contextmenu', this.contextMenu, false);
    }

    public componentDidUpdate(prevProps: FloorPlanSubPageProps, prevState: FloorPlanSubPageState): void {
        if (this.props.pageIndex === 2 && prevProps.pageIndex !== 2) {
            this.updateDimensions();
            const ctx: CanvasRenderingContext2D | null = this.imageCanvas.getContext('2d');
            if (ctx == null) {
                return;
            }
            ctx.clearRect(0, 0, this.imageCanvas.width, this.imageCanvas.height);
            this.setZoom(
                Math.min(
                    (this.imageCanvas.width / FloorPlanStore.image.width),
                    (this.imageCanvas.height / FloorPlanStore.image.height),
                    (this.imageCanvas.width / FloorPlanStore.image.height),
                    (this.imageCanvas.height / FloorPlanStore.image.width)
                ),
                null
            );
            const newPos: MouseCoords = this.centerImage(
                this.imageCanvas,
                FloorPlanStore.image.width,
                FloorPlanStore.image.height,
                this.props.store.cropZoom
            );
            this.props.store.setCropImagePosition(newPos.x, newPos.y);
            this.currentImage = this.state.image_id;
            this.redraw();
        }
    }

    public render(): JSX.Element | null {
        return (
            <div
                className="fp-canvas"
                ref={this.setDivElement}
                style={{display: (this.props.pageIndex === 2 ? 'block' : 'none')}}
            >
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.TOP_LEFT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.TOP_LEFT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.TOP_RIGHT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.TOP_RIGHT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.BOTTOM_LEFT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.BOTTOM_LEFT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.BOTTOM_RIGHT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.BOTTOM_RIGHT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.RIGHT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.RIGHT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.LEFT}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.LEFT, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.TOP}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.TOP, node); }}
                />
                <FloorPlanInteractor
                    type={FloorPlanInteractorType.BOTTOM}
                    ref={(node: FloorPlanInteractor) => { this.interactors.set(FloorPlanInteractorType.BOTTOM, node); }}
                />
                <canvas
                    style={{zIndex: 2}}
                    ref={(node: HTMLCanvasElement) => { this.overlayCanvas = node; }}
                />
                <canvas
                    style={{zIndex: 1}}
                    ref={(node: HTMLCanvasElement) => { this.imageCanvas = node; }}
                />
            </div>
        );
    }

    protected updateDimensions = (): void => {
        const width: number = this.divElement.clientWidth;
        const height: number = this.divElement.clientHeight;
        if (
            width != null &&
            height != null &&
            (
                this.imageCanvas.width !== width ||
                this.imageCanvas.height !== height ||
                this.imageCanvas.width !== this.overlayCanvas.width ||
                this.imageCanvas.height !== this.overlayCanvas.height
            )
        ) {
            this.imageCanvas.width = this.overlayCanvas.width = width;
            this.imageCanvas.height = this.overlayCanvas.height = height;
            this.props.store.cropPosition = {
                x1: .25 * width,
                y1: .25 * height,
                x2: .75 * width,
                y2: .75 * height
            };
        }
    };

    protected updateInteractors(): void {
        const width: number = Math.round(Math.abs(this.props.store.cropPosition.x1 - this.props.store.cropPosition.x2));
        const height: number = Math.round(Math.abs(this.props.store.cropPosition.y1 - this.props.store.cropPosition.y2));
        const sizeH: number = Math.round(Math.max(Math.min(40, width * .5), 4));
        const sizeV: number = Math.round(Math.max(Math.min(40, height * .5), 4));
        const validSize: boolean = this.props.store.validSize();

        this.interactors.forEach((interactor) => {interactor.update(
            width,
            height,
            sizeH,
            sizeV,
            validSize,
            this.props.store,
            this.draggingInfo.index
        ); });
    }

    protected redrawOverlay(): void {
        if (this.overlayCanvas != null) {
            const ctxOverlay: CanvasRenderingContext2D | null = this.overlayCanvas.getContext('2d');
            if (ctxOverlay == null) {
                return;
            }
            ctxOverlay.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
            ctxOverlay.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctxOverlay.fillRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
            ctxOverlay.fillStyle = BaseColor;
            const left: number = Math.round(Math.min(this.props.store.cropPosition.x1, this.props.store.cropPosition.x2));
            const top: number = Math.round(Math.min(this.props.store.cropPosition.y1, this.props.store.cropPosition.y2));
            const width: number = Math.round(Math.abs(this.props.store.cropPosition.x1 - this.props.store.cropPosition.x2));
            const height: number = Math.round(Math.abs(this.props.store.cropPosition.y1 - this.props.store.cropPosition.y2));
            // Clear crop-area
            ctxOverlay.clearRect(left, top, width, height);
        }
    }

    protected redraw = (noImage?: boolean): void => {
        this.updateDimensions();
        this.redrawOverlay();
        this.updateInteractors();
        if (!noImage) {
            this.redrawImage(true);
        }
    };

    protected pointerMove = (event: PointerEvent): void => {
        const coords: {x: number, y: number} = this.getPointerCoords(event);
        if (this.draggingInfo.index != null) {
            const position: CropPosition = {...this.props.store.cropPosition};
            let checkCropPosition: boolean = true;
            switch (this.draggingInfo.index) {
                case DragType.TOP:
                    position.y1 = coords.y - this.draggingInfo.y;
                    break;
                case DragType.BOTTOM:
                    position.y2 = coords.y - this.draggingInfo.y;
                    break;
                case DragType.LEFT:
                    position.x1 = coords.x - this.draggingInfo.x;
                    break;
                case DragType.RIGHT:
                    position.x2 = coords.x - this.draggingInfo.x;
                    break;
                case DragType.TOP_LEFT:
                    position.x1 = coords.x - this.draggingInfo.x;
                    position.y1 = coords.y - this.draggingInfo.y;
                    break;
                case DragType.TOP_RIGHT:
                    position.x2 = coords.x - this.draggingInfo.x;
                    position.y1 = coords.y - this.draggingInfo.y;
                    break;
                case DragType.BOTTOM_LEFT:
                    position.x1 = coords.x - this.draggingInfo.x;
                    position.y2 = coords.y - this.draggingInfo.y;
                    break;
                case DragType.BOTTOM_RIGHT:
                    position.x2 = coords.x - this.draggingInfo.x;
                    position.y2 = coords.y - this.draggingInfo.y;
                    break;
                default:
                    this.basePointerMove(event);
                    checkCropPosition = false;
            }
            if (checkCropPosition) {
                // Do not allow moving of interactors out of visible area (leave 20px space between interactor and border)
                if (position.x1 < 20) { position.x1 = 20; }
                if (position.y1 < 20) { position.y1 = 20; }
                if (position.x1 > (this.imageCanvas.width - 20)) { position.x1 = this.imageCanvas.width - 20; }
                if (position.y1 > (this.imageCanvas.height - 20)) { position.y1 = this.imageCanvas.height - 20; }

                if (position.x2 < 20) { position.x2 = 20; }
                if (position.y2 < 20) { position.y2 = 20; }
                if (position.x2 > (this.imageCanvas.width - 20)) { position.x2 = this.imageCanvas.width - 20; }
                if (position.y2 > (this.imageCanvas.height - 20)) { position.y2 = this.imageCanvas.height - 20; }
                this.props.store.cropPosition = position;
            }
            this.redraw(this.draggingInfo.index !== DragType.IMAGE && this.draggingInfo.index !== DragType.ZOOM);
        }
    };

    protected onChange = (): void => {
        const angle: number = FloorPlanStore.getState().angle;
        this.setState({
            image_id: FloorPlanStore.getState().image_id,
            angle: angle,
            unitString: ProjectSettingsStore.getState().unitOfMeasurement
        });
    };

    protected pointerUp = (event: PointerEvent): void => {
        if (this.zoomInfo.pointerId === event.pointerId && this.draggingInfo.index === DragType.ZOOM) {
            this.draggingInfo.index = DragType.IMAGE;
            this.redraw();
        } else {
            this.draggingInfo.index = null;
            this.redraw();
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/overlay/floorplan/FloorPlanCrop.tsx